import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleYesterdayPerformance,
  type HuddleYesterdayPerformanceResult,
  getHuddleYesterdayPerformanceOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddlePerformanceMeasuresToTableRows,
  mapHuddlePerformanceSummaryToMetrics,
  type HuddlePerformanceTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleYesterdayPerformanceOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleYesterdayPerformanceResult = {
  data: HuddleYesterdayPerformanceResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  tableRows: HuddlePerformanceTableRow[]
}

function useHuddleYesterdayPerformanceFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleYesterdayPerformanceOptions & {
  fetcher: HuddleDateFetcher<HuddleYesterdayPerformanceResult>
}): UseHuddleYesterdayPerformanceResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load yesterday performance',
  })

  const summaryMetrics = useMemo(
    () => mapHuddlePerformanceSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const tableRows = useMemo(
    () => mapHuddlePerformanceMeasuresToTableRows(data?.measures),
    [data?.measures],
  )

  return { data, loading, error, summaryMetrics, tableRows }
}

export function useHuddleYesterdayPerformance(
  options: UseHuddleYesterdayPerformanceOptions,
): UseHuddleYesterdayPerformanceResult {
  return useHuddleYesterdayPerformanceFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayPerformance,
  })
}
export function useHuddleYesterdayPerformanceOverview(
  options: UseHuddleYesterdayPerformanceOptions,
): UseHuddleYesterdayPerformanceResult {
  return useHuddleYesterdayPerformanceFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayPerformanceOverview,
  })
}
